import React from 'react';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';

import AutopayToggle from './AutopayToggle/AutopayToggle';
import Address from '../containers/Address';
import { prettyKey } from '../helpers/formatters';
import { toggleProcessingFeeExempt, toggleUserAutopay } from '../api';
import { toggleUserFeeExempt } from '../api';
import {
  findUser,
  setTaxExempt,
  toggleUserIsActive,
  updateSimoCycleType,
} from '../actions/users';

import {
  getLoggedInUser,
  getIsAdmin,
  getIncludedInGroups,
  getUserIsActiveErrorMessage,
} from '../reducers';
import { isB2b, isBusinessChild } from '../helpers/user';
import FeeExemptToggle from './FeeExemptToggle/FeeExemptToggle';
import IsActiveToggle from './IsActiveToggle/IsActiveToggle';
import TaxExemptToggle from './TaxExemptToggle/TaxExemptToggle';
import UserSimoCycleTypeToggle from './UserSimoCycleTypeToggle';
import PinnedNote from '../containers/PinnedNote';
import EditableRow from './EditableRow';
import Spinner from './Spinner';
import UpdateSimoAccountsModal from './modals/UpdateSimoAccountsModal';
import AddAddressModal from './modals/AddAddressModal';
import EditUserModal from './modals/EditUserModal';
import ProcessingFeeExemptToggle from './ProcessingFeeExemptToggle/ProcessingFeeExemptToggle';
import { can, permActions } from '../permissions';

const getActiveSubsCount = (subsCount) => {
  let count = 0;

  if (subsCount.att.active) {
    count += subsCount.att.active;
  }
  if (subsCount.att.bypass_active) {
    count += subsCount.att.bypass_active;
  }
  if (subsCount.simo.active) {
    count += subsCount.simo.active;
  }

  if (subsCount?.quick?.active) {
    count += subsCount.quick.active;
  }

  return count;
};

const generateLinkList = (isBusinessChildUser) => {
  const showAddress = true;
  const showAttSubs = !isBusinessChildUser;
  const showQuickSubs = !isBusinessChildUser;
  const showSimoSubs = !isBusinessChildUser;
  const showSimoDevices = !isBusinessChildUser;
  const showDeviceContracts = !isBusinessChildUser;
  const showNotes = true;
  let links = [
    {
      id: 1,
      title: 'Address',
      blockId: 'user-address',
      showLink: showAddress,
    },
    {
      id: 2,
      title: 'AT&T Subscriptions',
      blockId: 'att-subs',
      showLink: showAttSubs,
    },
    {
      id: 3,
      title: 'Quick Subscriptions',
      blockId: 'quick-subs',
      showLink: showQuickSubs,
    },
    {
      id: 4,
      title: 'Simo Subscriptions',
      blockId: 'simo-subs',
      showLink: showSimoSubs,
    },
    {
      id: 5,
      title: 'Simo Devices',
      blockId: 'simo-devices',
      showLink: showSimoDevices,
    },
    {
      id: 6,
      title: 'Device Contracts',
      blockId: 'device-contracts',
      showLink: showDeviceContracts,
    },
    {
      id: 7,
      title: 'Notes',
      blockId: 'notes-list',
      showLink: showNotes,
    },
  ];
  return links.filter((link) => link.showLink);
};

let User = ({
  user,
  isAdmin,
  isReseller,
  creatorUser,
  setTaxExempt,
  findUser,
  handleUpdateField,
  elementRef,
  userIsActiveErrorMessage,
  toggleUserIsActive,
  updateSimoCycleType,
  hasSimoSubs,
  scrollToBlock,
  subsCount,
  loading,
  canEditProfile,
  loggedInUser,
}) => {
  const isActive = user?.is_active;
  const canEditEmail = can(loggedInUser, permActions.EDIT_USER_EMAIL, {
    user,
  });
  const canEditGroup =
    isActive &&
    can(loggedInUser, permActions.EDIT_USER_GROUP, {
      user,
    });
  const isBusinessChildUser = isBusinessChild(user);

  const isB2bUser = isB2b(user);
  const showActiveSubsCount = isB2bUser;
  const showEnableAutoplay =
    isActive &&
    !isBusinessChildUser &&
    can(loggedInUser, permActions.CHANGE_AUTOPAY);
  const showFeeExemptToggle =
    isActive &&
    !isBusinessChildUser &&
    can(loggedInUser, permActions.CHANGE_PLAN_FEE_EXEMPT);
  const showSimoCycleTypeToggle =
    isActive &&
    !hasSimoSubs &&
    !isBusinessChildUser &&
    can(loggedInUser, permActions.CHANGE_USER_SIMO_CYCLE);
  const showUpdateSimoAccountModal =
    isActive &&
    !isBusinessChildUser &&
    can(loggedInUser, permActions.CHANGE_USER_SIMO_ACCOUNT);
  const activeSubsCount = getActiveSubsCount(subsCount);
  const quickLinks = generateLinkList(isBusinessChildUser);
  const canAddAddress = can(loggedInUser, permActions.CREATE_ADDRESS, {
    loggedInUser,
    userId: user.id,
  });
  const canDeactivateUser = can(loggedInUser, permActions.DEACTIVATE_USER, {
    loggedInUser,
    userId: user.id,
  });
  const showUpdateCreator =
    isActive && can(loggedInUser, permActions.UPDATE_CREATOR);
  const showChangeTaxExempt =
    isActive && isB2bUser && can(loggedInUser, permActions.CHANGE_TAX_EXEMPT);

  const renderRow = (attr) => {
    switch (attr) {
      case 'parent':
        return isReseller ? (
          <tr key={attr}>
            <td>
              <strong>{prettyKey(attr)}</strong>
            </td>
            <td>
              {typeof user[attr] !== 'undefined' &&
                user[attr] !== null &&
                user[attr].toString()}
            </td>
            <td></td>
          </tr>
        ) : (
          ''
        );
      case 'created_by':
        return (
          <tr key={attr}>
            <td>
              <strong>{prettyKey(attr)}</strong>
            </td>
            <td>
              {creatorUser &&
                `${creatorUser.first_name} ${creatorUser.last_name}`}
            </td>
            <td>
              {showUpdateCreator && (
                <Link to={`/users/${user.id}/update-creator`}>
                  Update creator
                </Link>
              )}
            </td>
          </tr>
        );
      case 'acl_group_name':
        return (
          <tr key={attr}>
            <td>
              <strong>{prettyKey(attr)}</strong>
            </td>
            <td>
              {typeof user[attr] !== 'undefined' &&
                user[attr] !== null &&
                user[attr].toString()}
            </td>
            <td>
              {canEditGroup && (
                <Link to={`/users/${user.id}/edit`}>Update user group</Link>
              )}
            </td>
          </tr>
        );
      default:
        const editableFields = [
          'email',
          'first_name',
          'last_name',
          'company_name',
          'phone_number',
        ];
        const notAllowedFields = [];
        if (!canEditEmail) {
          notAllowedFields.push('email');
        }

        if (
          isActive &&
          editableFields.includes(attr) &&
          !notAllowedFields.includes(attr) &&
          canEditProfile
        ) {
          return (
            <EditableRow
              key={attr}
              name={attr}
              label={prettyKey(attr)}
              value={user[attr]}
              type="input"
              save={handleUpdateField}
              thirdRowWidth="220px"
            />
          );
        } else {
          return (
            <tr key={attr}>
              <td>
                <strong>{prettyKey(attr)}</strong>
              </td>
              <td>
                {typeof user[attr] !== 'undefined' &&
                  user[attr] !== null &&
                  user[attr].toString()}
              </td>
              <td></td>
            </tr>
          );
        }
    }
  };

  return (
    <div ref={elementRef}>
      <div className="d-flex-vc">
        <h1 style={{ marginTop: '0' }}>
          User: {user.first_name} {user.last_name}
          {!user.is_active && <span className="inactive-user">(inactive)</span>}
        </h1>
        {user.is_active && (
          <div style={{ marginLeft: 'auto' }}>
            <EditUserModal user={user} />
          </div>
        )}
      </div>
      <div style={{ margin: '0 0 10px' }}>
        {quickLinks.map(({ id, title, blockId, showLink }) => (
          <React.Fragment key={id}>
            <button
              className="tab-btn"
              onClick={(e) => scrollToBlock(e, blockId)}
              style={{ padding: '0 5px' }}
            >
              <span>{title}</span>
            </button>
            {id !== quickLinks.length ? <span>&nbsp;|&nbsp;</span> : null}
          </React.Fragment>
        ))}
      </div>
      {showActiveSubsCount && (
        <div
          style={{
            display: 'flex',
            marginBottom: '10px',
          }}
        >
          <div
            style={{
              border: '1px solid #bce8f1',
              borderRadius: '4px',
              backgroundColor: '#d9edf7',
              padding: '2px 10px',
            }}
          >
            <b>Total Active Subscriptions: </b> {activeSubsCount}
          </div>
        </div>
      )}
      <PinnedNote />
      <div className="relative">
        <table className="table">
          <tbody>
            {[
              'id',
              'email',
              'first_name',
              'last_name',
              'company_name',
              'phone_number',
              'acl_group_name',
            ].map(renderRow)}
            <tr>
              <td>
                <strong>Is active</strong>
              </td>
              <td>{String(user.is_active)}</td>
              <td>
                {canDeactivateUser && (
                  <IsActiveToggle
                    userId={user.id}
                    status={user.is_active}
                    onToggle={toggleUserIsActive}
                    onToggleComplete={findUser}
                  />
                )}
                <br />
                <span className={'text-danger mar-t'}>
                  {userIsActiveErrorMessage?.detail}
                </span>
              </td>
            </tr>
            {[
              'has_password',
              'created_at',
              'created_by',
              'profile_image',
              'promo_code',
              'reference',
              'parent',
            ].map(renderRow)}
            <tr>
              <td>
                <strong>Autopay Enabled</strong>
              </td>
              <td>{String(user.autopay_enabled)}</td>
              <td>
                {showEnableAutoplay && (
                  <AutopayToggle
                    userId={user.id}
                    status={user.autopay_enabled}
                    onToggle={toggleUserAutopay}
                    onToggleComplete={findUser}
                  />
                )}
              </td>
            </tr>
            <tr>
              <td>
                <strong>Tax Exempt</strong>
              </td>
              <td>{String(user.tax_exempt_enabled)}</td>
              <td>
                {showChangeTaxExempt && (
                  <TaxExemptToggle
                    userId={user.id}
                    status={user.tax_exempt_enabled}
                    onToggle={setTaxExempt}
                  />
                )}
              </td>
            </tr>
            <tr>
              <td>
                <strong>Plan Fee Exempt</strong>
              </td>
              <td>{String(user.plan_fee_exempt)}</td>
              <td>
                {showFeeExemptToggle && (
                  <FeeExemptToggle
                    userId={user.id}
                    status={user.plan_fee_exempt}
                    onToggle={toggleUserFeeExempt}
                    onToggleComplete={findUser}
                  />
                )}
              </td>
            </tr>
            {isB2bUser && (
              <tr>
                <td>
                  <strong>Processing Fee Exempt</strong>
                </td>
                <td>{String(user.processing_fee_exempt)}</td>
                <td>
                  {isActive && isAdmin && (
                    <ProcessingFeeExemptToggle
                      userId={user.id}
                      status={user.processing_fee_exempt}
                      onToggle={toggleProcessingFeeExempt}
                      onToggleComplete={findUser}
                    />
                  )}
                </td>
              </tr>
            )}
            <tr>
              <td>
                <strong>Simo Cycle Type</strong>
              </td>
              <td>{String(user.simo_cycle_type)}</td>
              <td>
                {showSimoCycleTypeToggle && (
                  <UserSimoCycleTypeToggle
                    userId={user.id}
                    cycle_type={user.simo_cycle_type}
                    onToggle={updateSimoCycleType}
                  />
                )}
              </td>
            </tr>
            <tr>
              <td>
                <strong>Simo Account Name</strong>
              </td>
              <td>{user.simo_account_name}</td>
              <td>
                {showUpdateSimoAccountModal && (
                  <UpdateSimoAccountsModal
                    user={user}
                    text="Update Account Name"
                  />
                )}
              </td>
            </tr>
            <tr>
              <td>
                <strong>Simo Default Account Name</strong>
              </td>
              <td>{user.simo_default_account_name}</td>
              <td>
                {showUpdateSimoAccountModal && (
                  <UpdateSimoAccountsModal
                    user={user}
                    text="Update Default Account Name"
                  />
                )}
              </td>
            </tr>
          </tbody>
        </table>
        <Spinner showLoader={loading} />
      </div>

      {user.address && user.address.length > 0 && (
        <div>
          {user.address.map((address) => (
            <Address user={user} key={address.id} address={address} />
          ))}
        </div>
      )}
      {(!user.address || user.address.length === 0) && (
        <React.Fragment>
          <h4 id="user-address">Address</h4>
          {user.is_active && canAddAddress && <AddAddressModal user={user} />}
          {!user.is_active && <div>No Address</div>}
        </React.Fragment>
      )}
    </div>
  );
};

const mapStateToProps = (state, { user }) => ({
  loggedInUser: getLoggedInUser(state),
  isAdmin: getIsAdmin(state),
  isReseller: getIncludedInGroups(state, ['RESELLER']),
  isManager: getIncludedInGroups(state, ['MANAGER']),
  isAgentOrBilling: getIncludedInGroups(state, ['AGENT', 'BILLING']),
  userIsActiveErrorMessage: getUserIsActiveErrorMessage(state, user.id),
});

const mapDispatchToProps = {
  findUser,
  setTaxExempt,
  toggleUserIsActive,
  updateSimoCycleType,
};

User = connect(mapStateToProps, mapDispatchToProps)(User);

export default User;
